/*
 * A crew member's visit on the web (owner, question 68): the same as the phone app's visit screen, for crew who open the
 * Steward in a phone browser. Timer, checklist, photos, notes, On my way, Call the client, Complete and Undo. Once the
 * phone app is out (APP_LINK), the page sends them to it. Laid out like Jobber's visit for employees (owner shots,
 * 2026-10-30): Visit for the client, Directions and On my way, Visit, Details and Notes tabs, an Open Camera button.
 * Crew call the client from the round phone button at the top; texting the client is off (owner, Oct 31).
 */
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { yardLabel, type MyVisitDetail } from "@steward/shared";
import { api, ApiError } from "../api/client";
import { useSession } from "../app/session";
import { Banner, Button, Card, Skeleton, StatusLabel } from "../components/parts";
import { Modal } from "../components/overlays";
import { HeldPhotos } from "../components/HeldPhotos";
import { holdPhoto, worthKeeping } from "../components/photoOutbox";
import { shortDate, timeRange, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { DirectionsButton } from "../components/Directions";
import { PhoneRecord } from "../components/PhoneRecord";
import { useYardAsk } from "../components/YardAsk";

/** The phone app's install link, once it is in the stores. Null until then. */
const APP_LINK: string | null = null;

const WORDS = {
  en: {
    back: "Home",
    notFound: "This visit is not on your schedule, or it was moved. Go back to your day.",
    viewing: "You are seeing this visit the way they see it. Read only.",
    openMap: "Open in maps",
    onMyWay: "On my way",
    onMyWaySent: "Sent. The client knows you are coming.",
    start: "Start timer",
    stop: "Stop timer",
    checklist: "Checklist",
    photos: "Photos",
    addPhoto: "Add a photo",
    notes: "Notes",
    addNote: "Add note",
    complete: "Complete",
    undo: "Reopen Visit",
    directions: "Directions",
    visitFor: (n: string) => `Visit for ${n}`,
    tabVisit: "Visit",
    tabDetails: "Details",
    openCamera: "Open Camera",
    noNotes: "This visit has no notes",
    noPhotos: "No photos yet. Tap Open Camera.",
    noPhotosYet: "No photos yet.",
    work: "What to do",
    home: "The home",
    crewOn: "Crew",
    late: "Late",
    inProgress: "In progress",
    scheduled: "Scheduled",
    off: "Not happening",
    didPay: "Did the client pay you?",
    yes: "Yes",
    companyCheck: "Company check",
    payHint: "Cash comes off your pay. A company check goes to the owner.",
    no: "No",
    done: "Done",
    call: "Call the client",
    getApp: "Get the Steward app for your phone. It works better than the website.",
    wrong: "That didn't work. Try again.",
    must: "must do",
    mustLeft: (n: number) => (n === 1 ? "1 must-do item to tick" : `${String(n)} must-do items to tick`),
    photosLeft: (n: number) => (n === 1 ? "1 more photo to take" : `${String(n)} more photos to take`),
    finishFirst: "Finish these before Complete:",
    removePhoto: "Delete photo",
    removeAsk: "Delete this photo? It comes off the visit.",
    cancel: "Cancel",
  },
  es: {
    back: "Inicio",
    notFound: "Esta visita no está en tu horario, o se movió. Regresa a tu día.",
    viewing: "Estás viendo esta visita como la ve esa persona. Solo para ver.",
    openMap: "Abrir en el mapa",
    onMyWay: "Voy en camino",
    onMyWaySent: "Enviado. El cliente sabe que va en camino.",
    start: "Iniciar tiempo",
    stop: "Parar tiempo",
    checklist: "Lista",
    photos: "Fotos",
    addPhoto: "Agregar una foto",
    notes: "Notas",
    addNote: "Agregar nota",
    complete: "Terminar",
    undo: "Reabrir visita",
    directions: "Cómo llegar",
    visitFor: (n: string) => `Visita para ${n}`,
    tabVisit: "Visita",
    tabDetails: "Detalles",
    openCamera: "Abrir cámara",
    noNotes: "Esta visita no tiene notas",
    noPhotos: "No hay fotos. Toque Abrir cámara.",
    noPhotosYet: "No hay fotos.",
    work: "Qué hacer",
    home: "La casa",
    crewOn: "Equipo",
    late: "Atrasada",
    inProgress: "En curso",
    scheduled: "Programada",
    off: "No va",
    didPay: "¿El cliente le pagó?",
    yes: "Sí",
    companyCheck: "Cheque a la compañía",
    payHint: "El efectivo se descuenta de su pago. Un cheque a la compañía va al dueño.",
    no: "No",
    done: "Terminada",
    call: "Llamar al cliente",
    getApp: "Baje la app de Steward para su teléfono. Funciona mejor que la página web.",
    wrong: "Eso no funcionó. Intente otra vez.",
    must: "obligatorio",
    mustLeft: (n: number) => (n === 1 ? "1 punto obligatorio por marcar" : `${String(n)} puntos obligatorios por marcar`),
    photosLeft: (n: number) => (n === 1 ? "1 foto más por tomar" : `${String(n)} fotos más por tomar`),
    finishFirst: "Termine esto antes de Terminar:",
    removePhoto: "Borrar foto",
    removeAsk: "¿Borrar esta foto? Se quita de la visita.",
    cancel: "Cancelar",
  },
};

const path = (ref: string, rest = "") => `my-day/${encodeURIComponent(ref)}${rest}`;

/** A photo of my own visit, fetched with my sign in (an img tag cannot send the company). */
function Photo({ visitRef, id, name, label }: { visitRef: string; id: string; name: string; label: string | null }) {
  const { company } = useSession();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    void fetch(`/v1/${path(visitRef, `/photos/${id}`)}`, { credentials: "include", headers: { "x-company-id": company.id } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (b) {
          url = URL.createObjectURL(b);
          setSrc(url);
        }
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [visitRef, id, company.id]);
  const pic = src ? (
    <img src={src} alt={label ?? name} className="crew-photo" />
  ) : (
    <span className="crew-photo" aria-label={name} />
  );
  return label ? (
    <figure className="crew-shot">
      {pic}
      <figcaption>{label}</figcaption>
    </figure>
  ) : (
    pic
  );
}

/**
 * viewAs: an owner sees the visit as that crew member does (View as, owner 2026-11-02), read only: no timer, Complete,
 * On my way, checklist ticks, notes or photos.
 */
export function CrewVisit({ visitRef, viewAs }: { visitRef: string; viewAs?: string }) {
  const { me, company } = useSession();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"visit" | "details" | "notes">("visit");
  const w = WORDS[me.locale === "es" ? "es" : "en"];
  const qc = useQueryClient();
  const readOnly = Boolean(viewAs);
  const visit = useQuery({
    queryKey: ["myvisit", visitRef, viewAs ?? "me"],
    queryFn: () =>
      api<MyVisitDetail>(
        "GET",
        viewAs ? `team/${encodeURIComponent(viewAs)}/visits/${encodeURIComponent(visitRef)}` : path(visitRef),
      ),
    retry: false,
  });
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ["myvisit", visitRef] });
      await qc.invalidateQueries({ queryKey: ["myday"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : w.wrong),
  });
  const yard = useYardAsk();
  const v = visit.data;
  // Not on their schedule (or moved): say so, never a page that loads forever.
  if (visit.error)
    return (
      <div className="stack">
        <Banner type="notice">{w.notFound}</Banner>
        <Button
          type="secondary"
          onClick={() =>
            void (viewAs ? navigate({ to: "/hr/team/$personId/day", params: { personId: viewAs } }) : navigate({ to: "/my-day" }))
          }
        >
          {w.back}
        </Button>
      </div>
    );
  if (!v) return <Skeleton rows={6} />;
  const done = v.status === "done";
  const running = v.timerStartedAt !== null;
  // The checklist's must-dos and photos come before Complete (the server says the same).
  const mustLeft = v.checklist ? v.checklist.items.filter((i) => i.must && !i.done).length : 0;
  const photosLeft = v.checklist ? Math.max(0, v.checklist.photosNeeded - v.checklist.photosTaken) : 0;
  const complete = (paid?: "cash" | "company" | "no") => {
    setAsking(false);
    act.mutate(() => api("POST", path(visitRef, "/complete"), paid ? { crewCollected: paid } : {}));
  };
  const addPhoto = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = (typeof reader.result === "string" ? reader.result : "").split(",")[1] ?? "";
      yard.ask(1, (room) => {
        const body = { name: file.name, mime: file.type || "image/jpeg", dataBase64: data, ...(room ? { room } : {}) };
        act.mutate(async () => {
          try {
            await api("POST", path(visitRef, "/photos"), body);
          } catch (e) {
            // Kept on this phone to send again, under Photos (photoOutbox.ts), instead of lost.
            if (!worthKeeping(e)) throw e;
            holdPhoto(`crew:${visitRef}`, path(visitRef, "/photos"), body, e);
          }
        });
      });
    };
    reader.readAsDataURL(file);
  };

  const today = todayIn(company.timezone);
  const late = v.status === "scheduled" && v.date < today;
  const chip = done ? (
    <StatusLabel status="success">{w.done}</StatusLabel>
  ) : late ? (
    <StatusLabel status="critical">{w.late}</StatusLabel>
  ) : running || v.status === "in_progress" ? (
    <StatusLabel status="informative">{w.inProgress}</StatusLabel>
  ) : v.status === "skipped" || v.status === "cancelled" ? (
    <StatusLabel status="inactive">{w.off}</StatusLabel>
  ) : (
    <StatusLabel status="warning">{w.scheduled}</StatusLabel>
  );
  const cameraInput = (
    <input
      type="file"
      accept="image/*"
      capture="environment"
      hidden
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) addPhoto(f);
        e.target.value = "";
      }}
    />
  );

  return (
    <PhoneRecord
      back={{
        label: w.back,
        go: () =>
          void (viewAs ? navigate({ to: "/hr/team/$personId/day", params: { personId: viewAs } }) : navigate({ to: "/my-day" })),
      }}
      kind="truck"
      status={chip}
      title={w.visitFor(v.clientName)}
      sub={<span className="prec-sub-muted">{v.jobName}</span>}
      contactExtra={
        v.clientPhone ? (
          <a href={`tel:${v.clientPhone.replace(/[^0-9+]/g, "")}`} aria-label={w.call}>
            <Icon name="phone" size={20} />
          </a>
        ) : null
      }
      facts={[
        ...(v.address
          ? [
              {
                key: "where",
                icon: "directions" as const,
                main: v.address,
                after: (
                  <>
                    <DirectionsButton address={v.address} lang={me.locale === "es" ? "es" : "en"}>
                      {w.directions}
                    </DirectionsButton>
                    {!done && !readOnly ? (
                      <Button
                        type="secondary"
                        onClick={() =>
                          act.mutate(async () => {
                            await api("POST", path(visitRef, "/on-my-way"), {});
                            setInfo(w.onMyWaySent);
                          })
                        }
                      >
                        {w.onMyWay}
                      </Button>
                    ) : null}
                  </>
                ),
              },
            ]
          : []),
        {
          key: "when",
          icon: "schedule" as const,
          main: (
            <>
              {shortDate(v.date)}, {timeRange(v.startTime, v.endTime)}
            </>
          ),
        },
      ]}
      actions={
        readOnly ? null : done ? (
          <Button
            type="secondary"
            loading={act.isPending}
            onClick={() => act.mutate(() => api("POST", path(visitRef, "/undo-complete"), {}))}
          >
            {w.undo}
          </Button>
        ) : (
          <>
            <Button
              loading={act.isPending}
              disabled={mustLeft > 0 || photosLeft > 0}
              onClick={() => (v.asksCashOrCheck ? setAsking(true) : complete())}
            >
              {w.complete}
            </Button>
            <Button
              type="secondary"
              icon={running ? "stop" : "play"}
              onClick={() => act.mutate(() => api("POST", path(visitRef, "/timer"), { action: running ? "stop" : "start" }))}
            >
              {running ? w.stop : w.start}
            </Button>
          </>
        )
      }
      tabs={{
        tabs: [
          { key: "visit", label: w.tabVisit },
          { key: "details", label: w.tabDetails },
          { key: "notes", label: w.notes },
        ],
        active: tab,
        onChange: (k) => setTab(k as typeof tab),
      }}
      fab={
        readOnly ? null : (
          <label className="btn primary">
            {cameraInput}
            {w.openCamera}
            <Icon name="camera" size={22} />
          </label>
        )
      }
    >
      {yard.sheet}
      {readOnly ? <Banner type="notice">{w.viewing}</Banner> : null}
      {APP_LINK && !readOnly ? <Banner action={<a href={APP_LINK}>Get the app</a>}>{w.getApp}</Banner> : null}
      {info ? <Banner type="success">{info}</Banner> : null}
      {error ? <Banner type="error">{error}</Banner> : null}
      {!done && (mustLeft || photosLeft) ? (
        <Banner type="warning">
          {w.finishFirst}{" "}
          {[mustLeft ? w.mustLeft(mustLeft) : "", photosLeft ? w.photosLeft(photosLeft) : ""].filter(Boolean).join(", ")}.
        </Banner>
      ) : null}
      {/* Complete asks the question in a box over the page (owner, Oct 31), not laid out on it. */}
      <Modal title={w.didPay} open={asking} onClose={() => setAsking(false)}>
        <div className="stack">
          <p className="muted small">{w.payHint}</p>
          <Button size="large" fullWidth onClick={() => complete("cash")}>
            {w.yes}
          </Button>
          <Button size="large" fullWidth onClick={() => complete("company")}>
            {w.companyCheck}
          </Button>
          <Button size="large" fullWidth type="secondary" onClick={() => complete("no")}>
            {w.no}
          </Button>
        </div>
      </Modal>
      <Modal
        title={w.removePhoto}
        open={removing !== null}
        onClose={() => setRemoving(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setRemoving(null)}>
              {w.cancel}
            </Button>
            <Button
              variation="destructive"
              loading={act.isPending}
              onClick={() => {
                const id = removing;
                setRemoving(null);
                if (id) act.mutate(() => api("DELETE", path(visitRef, `/photos/${id}`)));
              }}
            >
              {w.removePhoto}
            </Button>
          </>
        }
      >
        <p>{w.removeAsk}</p>
      </Modal>

      {tab === "visit" ? (
        <>
          {v.work.length || v.crewNotes || v.home ? (
            <Card title={w.work}>
              {v.home ? (
                <p>
                  <b>{w.home}</b> · {v.home}
                </p>
              ) : null}
              {v.work.map((x, i) => (
                <p key={String(i)}>{x}</p>
              ))}
              {v.crewNotes ? <p>{v.crewNotes}</p> : null}
            </Card>
          ) : null}

          {v.checklist ? (
            <Card title={v.checklist.name || w.checklist}>
              {v.checklist.items.map((i) => (
                <label key={`${i.id}:${String(i.copy)}`} className="check-row">
                  <input
                    type="checkbox"
                    checked={i.done}
                    disabled={done || readOnly}
                    onChange={(e) =>
                      act.mutate(() =>
                        api("POST", path(visitRef, "/checklist"), { itemId: i.id, done: e.target.checked, copy: i.copy }),
                      )
                    }
                  />
                  {i.room ? <b>{i.room}: </b> : null}
                  {i.label}
                  {i.must ? <span className="muted small"> ({w.must})</span> : null}
                </label>
              ))}
            </Card>
          ) : null}

          <Card title={w.photos}>
            <HeldPhotos at={`crew:${visitRef}`} lang={me.locale === "es" ? "es" : "en"} />
            {v.photos.length ? (
              <div className="crew-photos">
                {v.photos.map((p) => (
                  <div key={p.id} className="crew-photo-wrap">
                    {readOnly ? (
                      <span className="muted small">{p.name}</span>
                    ) : (
                      <Photo
                        visitRef={v.ref}
                        id={p.id}
                        name={p.name}
                        label={yardLabel(p.room, me.locale === "es" ? "es" : "en")}
                      />
                    )}
                    {p.mine && !readOnly ? (
                      <button type="button" className="crew-photo-x" aria-label={w.removePhoto} onClick={() => setRemoving(p.id)}>
                        <Icon name="close" size={16} />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">{readOnly ? w.noPhotosYet : w.noPhotos}</p>
            )}
          </Card>
        </>
      ) : tab === "details" ? (
        <section className="card">
          <div className="prec-list">
            <div className="prec-row">
              <span>
                <b>
                  {v.clientName} - {v.jobName}
                </b>
              </span>
            </div>
            <div className="prec-row">
              <Icon name="person" size={22} />
              <span>
                <b>{v.clientName}</b>
                {v.address ? <span className="muted">{v.address}</span> : null}
              </span>
            </div>
            {v.crew.length ? (
              <div className="prec-row">
                <Icon name="team" size={22} />
                <span>
                  <b>{w.crewOn}</b>
                  <span className="muted">{v.crew.join(", ")}</span>
                </span>
              </div>
            ) : null}
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="prec-list">
            {v.notes.length ? (
              v.notes.map((n) => (
                <div key={n.id} className="prec-row">
                  <span>
                    {n.authorName ? <b>{n.authorName}</b> : null}
                    <span>{n.body}</span>
                  </span>
                </div>
              ))
            ) : (
              <p className="prec-empty">{w.noNotes}</p>
            )}
          </div>
          {readOnly ? null : (
            <div className="prec-note-add">
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} aria-label={w.addNote} />
              <div className="row">
                <Button
                  type="secondary"
                  disabled={!note.trim()}
                  onClick={() =>
                    act.mutate(async () => {
                      await api("POST", path(visitRef, "/notes"), { body: note.trim() });
                      setNote("");
                    })
                  }
                >
                  {w.addNote}
                </Button>
                <label className="btn secondary icon-only" aria-label={w.addPhoto}>
                  {cameraInput}
                  <Icon name="camera" size={22} />
                </label>
              </div>
            </div>
          )}
        </section>
      )}
    </PhoneRecord>
  );
}
